import FileSaver from 'file-saver'
import React, { useState } from 'react'
import { Button, Dropdown } from 'react-bootstrap'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import * as reports from '../../_augmentt/ducks/reports'
import { getSelectedCustomer, getSelectedCustomerName } from '../../_augmentt/selectors/customers'
import { delay } from '../../_augmentt/utils/utils'
import { createReport, fetchReportData, onDownloadFileClick } from '../crud/report.crud'
import { emitReportExportAuditLog } from '../util/audit.util'
import { getCsvBlob } from '../util/excel'
import { getReportFilename } from '../util/string.utils'
import DisabledExportButton from './DisabledExportButton/DisabledExportButton'

const createReportFlow = async ({
  dateRange,
  dispatch,
  intl,
  setLoading,
  reportPayload,
  sort,
  selectedCustomer: { id: customer, customer_name: customerName },
  onExportComplete,
}) => {
  setLoading(true)
  dispatch(reports.actions.setReportError({ status: false, type: null, data: null }))

  const reportFilename = getReportFilename(customerName, 'MENU.LICENSE_REPORT', 'pdf', intl)
  try {
    let response = await createReport({
      customer,
      dateRange,
      reportPayload,
      sort,
      reportName: 'License Report',
      reportType: 'license',
    })

    const reportId = response?.data?.id
    if (!reportId) {
      throw new Error('No report id!')
    }

    let complete = false
    let retries = 20
    while (!complete && retries > 0) {
      await delay(3000) // eslint-disable-line no-await-in-loop
      response = await fetchReportData(reportId) // eslint-disable-line no-await-in-loop
      complete = response.data.complete
      retries -= 1
    }

    if (!complete) {
      throw new Error('Maximum retries reached!')
    } else if (response?.data?.error || !response?.data?.url) {
      throw new Error('Error generating report!')
    }

    await onDownloadFileClick(response.data.url, reportFilename, 'pdf', () => {
      onExportComplete?.({
        format: 'pdf',
        reportName: 'License Report',
        reportType: 'license',
      })
    })
  } catch (err) {
    console.error('An error occurred while creating the report.', err)
    dispatch(
      reports.actions.setReportError({
        status: true,
        type: 'generic',
      }),
    )
  }

  setLoading(false)
}

function ExportLicenseWidget() {
  const intl = useIntl()
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()

  const [loading, setLoading] = useState(false)

  const selectedCustomer = useSelector(getSelectedCustomer)
  const selectedCustomerName = useSelector(getSelectedCustomerName)
  const organizationId = useSelector(({ user }) => user?.data?.organization)
  const { sort } = useSelector(({ exporter }) => exporter)
  const reportPayload = useSelector(({ exporter }) => exporter.report)
  const exportUnavailable = useSelector(({ exporter }) => exporter.exportUnavailable)

  const dateRange = useSelector(({ dates }) => dates.data)

  const emitLicenseExportAuditLog = ({ format, reportName = 'License Report', reportType = 'license' }) =>
    emitReportExportAuditLog({
      organizationId,
      customerId: selectedCustomer?.id,
      customerName: selectedCustomerName,
      reportName,
      reportType,
      source: 'LICENSE_REPORT',
      format,
      outputCount: 1,
      path: location.pathname,
      component: 'ExportLicenseWidget',
    })

  const onCreateReport = (fileExtension) => {
    if (fileExtension === 'pdf') {
      createReportFlow({
        dateRange,
        dispatch,
        intl,
        setLoading,
        reportPayload,
        sort,
        selectedCustomer,
        onExportComplete: emitLicenseExportAuditLog,
      })
    }

    if (fileExtension === 'csv') {
      FileSaver.saveAs(
        getCsvBlob(reportPayload, 'LICENSE_REPORT', sort, intl),
        getReportFilename(selectedCustomerName, 'MENU.LICENSE_REPORT', fileExtension, intl),
      )
      emitLicenseExportAuditLog({ format: 'csv' })
    }
  }

  if (loading) {
    return <Button variant="light" className="spinner spinner--center spinner--md spinner--primary" />
  }

  if (exportUnavailable) {
    return <DisabledExportButton />
  }

  return (
    <Dropdown drop="down" className="ms-3" style={{ padding: 0 }}>
      <Dropdown.Toggle variant="btn" className="btn-light" id="dropdownToggleExport" style={{ marginLeft: 0 }}>
        <FormattedMessage id="GENERAL.EXPORT" />
      </Dropdown.Toggle>
      <Dropdown.Menu className="dropdown-menu-fit dropdown-menu-end dropdown-menu-anim">
        <ul className="nav margin-t-10 margin-b-10">
          <li className="nav__item" data-bs-toggle="collapse">
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <span onClick={() => onCreateReport('csv')} className="nav__link" id="export-csv-license-report">
              <span className="nav__link nav__link-text">
                <FormattedMessage id="GENERAL.CSV" />
              </span>
            </span>
          </li>
          <li className="nav__item" data-bs-toggle="collapse">
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <span onClick={() => onCreateReport('pdf')} className="nav__link" id="export-pdf-license-report">
              <span className="nav__link nav__link-text">
                <FormattedMessage id="GENERAL.PDF" />
              </span>
            </span>
          </li>
          <li className="nav__item" data-bs-toggle="collapse">
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <span
              onClick={() => history.push('/scheduled-reports')}
              className="nav__link"
              id="export-scheduled-reports"
            >
              <span className="nav__link nav__link-text">
                <FormattedMessage id="MENU.SCHEDULED_REPORTS" />
              </span>
            </span>
          </li>
        </ul>
      </Dropdown.Menu>
    </Dropdown>
  )
}

export default ExportLicenseWidget
